import type { FourTransformationEdge, MutagenId, NormalizedChart, PalaceId, StarId } from './domain.js';

export const MUTAGEN_LABELS_ZH_TW: Readonly<Record<MutagenId, '祿' | '權' | '科' | '忌'>> = Object.freeze({
  lu: '祿',
  quan: '權',
  ke: '科',
  ji: '忌',
});

export interface BirthYearTransformationEvidence {
  kind: 'birth_year';
  featureId: string;
  evidenceRef: string;
  mutagen: MutagenId;
  mutagenLabel: '祿' | '權' | '科' | '忌';
  starId: StarId;
  starLabel: string;
  targetPalaceId: PalaceId;
  targetPalaceLabel: string;
}

export interface PalaceStemTransformationEvidence {
  kind: 'palace_stem_flight';
  featureId: string;
  evidenceRef: string;
  sourcePalaceId: PalaceId;
  sourcePalaceLabel: string;
  sourceHeavenlyStem: string;
  mutagen: MutagenId;
  mutagenLabel: '祿' | '權' | '科' | '忌';
  starId: StarId;
  starLabel: string;
  targetPalaceId: PalaceId;
  targetPalaceLabel: string;
  rulesetVersion: string;
}

export interface TransformationEvidenceFilter {
  targetPalaceId?: PalaceId;
  sourcePalaceId?: PalaceId;
  mutagen?: MutagenId;
  starId?: StarId;
  kinds?: readonly ('birth_year' | 'palace_stem_flight')[];
}

export interface TransformationEvidenceViewModel {
  birthYear: readonly BirthYearTransformationEvidence[];
  palaceStemFlights: readonly PalaceStemTransformationEvidence[];
  counts: {
    birthYear: number;
    palaceStemFlights: number;
  };
  disclosure: string;
}

function allStars(chart: NormalizedChart, palaceId: PalaceId) {
  const palace = chart.palaces[palaceId];
  return [...palace.majorStars, ...palace.minorStars, ...palace.adjectiveStars];
}

function birthYearEvidenceRef(palaceId: PalaceId, starId: StarId, mutagen: MutagenId): string {
  return `NATAL:PALACE:${palaceId}:STAR:${starId}:MUTAGEN:${mutagen}`.toUpperCase();
}

function palaceStemEvidenceRef(edge: FourTransformationEdge): string {
  return `NATAL:PALACE-STEM-FLIGHT:${edge.sourcePalaceId}:${edge.mutagen}:${edge.targetPalaceId}:${edge.starId}`.toUpperCase();
}

function compareBirthYear(a: BirthYearTransformationEvidence, b: BirthYearTransformationEvidence): number {
  return a.targetPalaceId.localeCompare(b.targetPalaceId)
    || a.mutagen.localeCompare(b.mutagen)
    || a.starId.localeCompare(b.starId);
}

function comparePalaceStem(a: PalaceStemTransformationEvidence, b: PalaceStemTransformationEvidence): number {
  return a.sourcePalaceId.localeCompare(b.sourcePalaceId)
    || a.mutagen.localeCompare(b.mutagen)
    || a.targetPalaceId.localeCompare(b.targetPalaceId)
    || a.starId.localeCompare(b.starId);
}

function includeKind(filter: TransformationEvidenceFilter | undefined, kind: 'birth_year' | 'palace_stem_flight'): boolean {
  return !filter?.kinds || filter.kinds.includes(kind);
}

export function buildTransformationEvidenceViewModel(
  chart: NormalizedChart,
  filter?: TransformationEvidenceFilter,
): TransformationEvidenceViewModel {
  const birthYear: BirthYearTransformationEvidence[] = [];
  const palaceStemFlights: PalaceStemTransformationEvidence[] = [];

  if (includeKind(filter, 'birth_year') && !filter?.sourcePalaceId) {
    for (const palaceId of chart.palaceOrder) {
      const palace = chart.palaces[palaceId];
      for (const star of allStars(chart, palaceId)) {
        for (const mutagen of star.mutagens) {
          if (filter?.targetPalaceId && filter.targetPalaceId !== palaceId) continue;
          if (filter?.mutagen && filter.mutagen !== mutagen) continue;
          if (filter?.starId && filter.starId !== star.id) continue;
          birthYear.push({
            kind: 'birth_year',
            featureId: `palace.${palace.id}.star.${star.id}.birth_year_mutagen.${mutagen}`,
            evidenceRef: birthYearEvidenceRef(palace.id, star.id, mutagen),
            mutagen,
            mutagenLabel: MUTAGEN_LABELS_ZH_TW[mutagen],
            starId: star.id,
            starLabel: star.label,
            targetPalaceId: palace.id,
            targetPalaceLabel: palace.label,
          });
        }
      }
    }
  }

  if (includeKind(filter, 'palace_stem_flight')) {
    for (const edge of chart.natalTransformations) {
      if (filter?.sourcePalaceId && filter.sourcePalaceId !== edge.sourcePalaceId) continue;
      if (filter?.targetPalaceId && filter.targetPalaceId !== edge.targetPalaceId) continue;
      if (filter?.mutagen && filter.mutagen !== edge.mutagen) continue;
      if (filter?.starId && filter.starId !== edge.starId) continue;
      const source = chart.palaces[edge.sourcePalaceId];
      const target = chart.palaces[edge.targetPalaceId];
      const star = allStars(chart, edge.targetPalaceId).find((candidate) => candidate.id === edge.starId);
      palaceStemFlights.push({
        kind: 'palace_stem_flight',
        featureId: `palace_stem_flight.${edge.sourcePalaceId}.${edge.mutagen}.${edge.targetPalaceId}.${edge.starId}`,
        evidenceRef: palaceStemEvidenceRef(edge),
        sourcePalaceId: edge.sourcePalaceId,
        sourcePalaceLabel: source.label,
        sourceHeavenlyStem: edge.sourceHeavenlyStem,
        mutagen: edge.mutagen,
        mutagenLabel: MUTAGEN_LABELS_ZH_TW[edge.mutagen],
        starId: edge.starId,
        starLabel: star?.label ?? edge.starId,
        targetPalaceId: edge.targetPalaceId,
        targetPalaceLabel: target.label,
        rulesetVersion: edge.rulesetVersion,
      });
    }
  }

  birthYear.sort(compareBirthYear);
  palaceStemFlights.sort(comparePalaceStem);

  return {
    birthYear,
    palaceStemFlights,
    counts: { birthYear: birthYear.length, palaceStemFlights: palaceStemFlights.length },
    disclosure: '生年四化與宮干飛化屬不同計算脈絡：前者標記出生年天干形成的四化星，後者描述各宮天干起化後飛入的目標宮位。本視圖只呈現可追溯盤面事實，不自行判定吉凶。',
  };
}

export interface TransformationIntegrityIssue {
  code:
    | 'invalid_edge_scope'
    | 'unknown_source_palace'
    | 'unknown_target_palace'
    | 'source_stem_mismatch'
    | 'unknown_target_star'
    | 'duplicate_source_mutagen'
    | 'missing_source_mutagen'
    | 'unexpected_edge_count'
    | 'ruleset_version_mismatch';
  path: string;
  message: string;
}

export function validateCompletePalaceStemTransformations(chart: NormalizedChart): TransformationIntegrityIssue[] {
  const issues: TransformationIntegrityIssue[] = [];
  const required: readonly MutagenId[] = ['lu', 'quan', 'ke', 'ji'];
  const seen = new Set<string>();

  if (chart.natalTransformations.length !== chart.palaceOrder.length * required.length) {
    issues.push({
      code: 'unexpected_edge_count',
      path: 'chart.natalTransformations',
      message: `Expected ${chart.palaceOrder.length * required.length} palace-stem transformation edges, got ${chart.natalTransformations.length}.`,
    });
  }

  for (const [index, edge] of chart.natalTransformations.entries()) {
    const path = `chart.natalTransformations[${index}]`;
    const source = chart.palaces[edge.sourcePalaceId];
    const target = chart.palaces[edge.targetPalaceId];
    if (edge.scope !== 'natal') {
      issues.push({ code: 'invalid_edge_scope', path: `${path}.scope`, message: 'Natal palace-stem transformations must use natal scope.' });
    }
    if (!source) {
      issues.push({ code: 'unknown_source_palace', path: `${path}.sourcePalaceId`, message: `Unknown source palace: ${edge.sourcePalaceId}.` });
    } else if (source.heavenlyStem !== edge.sourceHeavenlyStem) {
      issues.push({ code: 'source_stem_mismatch', path: `${path}.sourceHeavenlyStem`, message: `Source stem ${edge.sourceHeavenlyStem} does not match ${source.id} palace stem ${source.heavenlyStem}.` });
    }
    if (!target) {
      issues.push({ code: 'unknown_target_palace', path: `${path}.targetPalaceId`, message: `Unknown target palace: ${edge.targetPalaceId}.` });
    } else if (!allStars(chart, target.id).some((star) => star.id === edge.starId)) {
      issues.push({ code: 'unknown_target_star', path: `${path}.starId`, message: `Target star ${edge.starId} is not present in ${target.id}.` });
    }
    if (edge.rulesetVersion !== chart.calculation.rulesetVersion) {
      issues.push({ code: 'ruleset_version_mismatch', path: `${path}.rulesetVersion`, message: `Edge ruleset ${edge.rulesetVersion} differs from chart ruleset ${chart.calculation.rulesetVersion}.` });
    }
    const key = `${edge.sourcePalaceId}:${edge.mutagen}`;
    if (seen.has(key)) {
      issues.push({ code: 'duplicate_source_mutagen', path, message: `Duplicate palace-stem transformation for ${key}.` });
    }
    seen.add(key);
  }

  for (const palaceId of chart.palaceOrder) {
    for (const mutagen of required) {
      if (!seen.has(`${palaceId}:${mutagen}`)) {
        issues.push({
          code: 'missing_source_mutagen',
          path: `chart.natalTransformations.${palaceId}.${mutagen}`,
          message: `Missing ${mutagen} palace-stem transformation from ${palaceId}.`,
        });
      }
    }
  }

  return issues.sort((a, b) => a.path.localeCompare(b.path) || a.code.localeCompare(b.code));
}

export function assertCompletePalaceStemTransformations(chart: NormalizedChart): void {
  const issues = validateCompletePalaceStemTransformations(chart);
  if (issues.length > 0) {
    throw new Error(`Invalid palace-stem transformations: ${issues.map((issue) => `${issue.path}(${issue.code})`).join(', ')}`);
  }
}
